import { containsMathValueToken } from './mathValueToken';
import {
  isArrayVariable,
  isNumberVariable,
  normalizeArrayValues,
  normalizeNumberValue,
  sanitizeNumberInput,
  VARIABLE_TYPE_ARRAY,
  VARIABLE_TYPE_NUMBER,
  VARIABLE_TYPE_STRING,
} from './variableScope';

export const VARIABLE_TYPE_CHOICES = [
  {
    type: VARIABLE_TYPE_STRING,
    label: 'Text',
    hint: 'One value — {{name}}',
  },
  {
    type: VARIABLE_TYPE_NUMBER,
    label: 'Number',
    hint: 'Digits only — {{name}}',
  },
  {
    type: VARIABLE_TYPE_ARRAY,
    label: 'Array',
    hint: 'Multiple values — {{name[1]}}',
  },
];

export const variableTypeOf = (row) => {
  if (isArrayVariable(row)) return VARIABLE_TYPE_ARRAY;
  if (isNumberVariable(row)) return VARIABLE_TYPE_NUMBER;
  return VARIABLE_TYPE_STRING;
};

export const variableTypeLabel = (type) =>
  VARIABLE_TYPE_CHOICES.find((c) => c.type === type)?.label || 'Text';

/** Empty, a live math token, or digits with optional leading - / one dot. */
export const isNumberCompatibleValue = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return true;
  if (containsMathValueToken(text)) return true;
  return sanitizeNumberInput(text) === text && normalizeNumberValue(text) !== '';
};

const numberValueFrom = (raw) => {
  const text = String(raw ?? '').trim();
  if (containsMathValueToken(text)) return text;
  return normalizeNumberValue(text);
};

const asStringRow = (row, value) => {
  const next = { ...row, type: VARIABLE_TYPE_STRING, value: String(value ?? '') };
  delete next.values;
  return next;
};

const asNumberRow = (row, value) => {
  const next = { ...row, type: VARIABLE_TYPE_NUMBER, value: String(value ?? '') };
  delete next.values;
  return next;
};

const asArrayRow = (row, values) => ({
  ...row,
  type: VARIABLE_TYPE_ARRAY,
  values: normalizeArrayValues(values),
  value: '',
});

export const buildVariableOfType = (base, type) => {
  if (type === VARIABLE_TYPE_ARRAY) {
    return asArrayRow(base, ['']);
  }
  if (type === VARIABLE_TYPE_NUMBER) {
    return asNumberRow(base, '');
  }
  return asStringRow(base, '');
};

/**
 * Plan a type change.
 * `{ needsConfirm, message, next }` — apply `next` immediately, or after confirm.
 */
export const planVariableTypeChange = (row, nextType) => {
  const from = variableTypeOf(row);
  const to = nextType || VARIABLE_TYPE_STRING;
  if (from === to) {
    return { needsConfirm: false, message: '', next: row };
  }

  if (to === VARIABLE_TYPE_STRING) {
    if (from === VARIABLE_TYPE_NUMBER) {
      return { needsConfirm: false, message: '', next: asStringRow(row, row.value) };
    }
    const slots = normalizeArrayValues(row.values);
    const filled = slots.filter((s) => String(s ?? '').trim() !== '');
    if (filled.length > 1) {
      return {
        needsConfirm: true,
        message:
          'This array has multiple values. Changing to text will clear them.',
        next: asStringRow(row, ''),
      };
    }
    return {
      needsConfirm: false,
      message: '',
      next: asStringRow(row, slots[0] ?? ''),
    };
  }

  if (to === VARIABLE_TYPE_NUMBER) {
    if (from === VARIABLE_TYPE_STRING || from === VARIABLE_TYPE_NUMBER) {
      if (isNumberCompatibleValue(row.value)) {
        return {
          needsConfirm: false,
          message: '',
          next: asNumberRow(row, numberValueFrom(row.value)),
        };
      }
      return {
        needsConfirm: true,
        message: 'This value is not a number. Changing type will clear it.',
        next: asNumberRow(row, ''),
      };
    }
    const slots = normalizeArrayValues(row.values);
    const filled = slots.filter((s) => String(s ?? '').trim() !== '');
    if (filled.length <= 1 && isNumberCompatibleValue(slots[0])) {
      return {
        needsConfirm: false,
        message: '',
        next: asNumberRow(row, numberValueFrom(slots[0])),
      };
    }
    return {
      needsConfirm: true,
      message: 'This array cannot become a number as-is. Changing type will clear it.',
      next: asNumberRow(row, ''),
    };
  }

  // → array
  if (from === VARIABLE_TYPE_ARRAY) {
    return { needsConfirm: false, message: '', next: asArrayRow(row, row.values) };
  }
  return {
    needsConfirm: false,
    message: '',
    next: asArrayRow(row, [String(row.value ?? '')]),
  };
};
